import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import { DatePicker, Dropdown, Empty, Menu, message, Popconfirm, Space, Typography } from "antd";
import ListTable from "common/ListTable";
import moment from "moment";
import {
  DAILY_LINE_PLAN,
  ImportDailyLinePlan,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  DAILY_LINE_PLAN_DELETE_PERMISSION,
  DAILY_LINE_PLAN_IMPORT_PERMISSION,
  NO_PERMISSION_REQUIRED,
} from "routes/permissions";
import { cglFormatDate, hasPermission } from "utils/functions";
import "./index.css";
const DailyLinePlanList = () => {
  // Router
  const navigate = useNavigate();
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [filterValues, setFilterValues] = useState({});

  // ux2(C1): `date` has no default filter today; open on the last 30 days so the page isn't
  // blank, while the endpoint stays backward compatible when from_date/to_date are absent.
  const getDefaultDateRange = () => [moment().subtract(29, "days"), moment()];
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Guard against setState after unmount and against a slower, stale request clobbering a
  // faster, later one (round-1 pattern).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const reqIdRef = useRef(0);

  const refetch = () => {
    setRefresh((prev) => prev + 1);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const getAppRoleMenu = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);

    const query = `${DAILY_LINE_PLAN}`;
    const bodyData = {
      page: currentPage,
      ...(filterValues?.style_id && { style_id: filterValues?.style_id }),
      ...(filterValues?.line_id && { line_id: filterValues?.line_id }),
      ...(filterValues?.buyer_id && { buyer_id: filterValues?.buyer_id }),
      ...(filterValues?.sub_style_id && {
        sub_style_id: filterValues?.sub_style_id,
      }),
      ...(dateRange?.[0] && dateRange?.[1] && {
        from_date: dateRange[0].format("YYYY-MM-DD"),
        to_date: dateRange[1].format("YYYY-MM-DD"),
      }),
    };

    const response = await getData(query, false, bodyData);

    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response?.status === 200) {
      setLoading(false);
      setMetaData({
        defaultPageSize: response?.data?.data?.per_page,
        current: response?.data?.data?.current_page,
        defaultCurrent: response?.data?.data?.current_page,
        total: response?.data?.data?.total,
      });
      setTableData(response?.data?.data?.data);
    } else {
      setLoading(false);
      message.error("Error getting vehicle info data");
    }
  }, [currentPage, filterValues, dateRange]);

  const handleDateRangeChange = (dates) => {
    setCurrentPage(1);
    setDateRange(dates && dates[0] && dates[1] ? dates : getDefaultDateRange());
  };

  const onAdd = () => {
    navigate("/digitalization/daily-line-plan/add");
  };

  const onEdit = (id) => {
    navigate(`/digitalization/daily-line-plan/edit/${id}`);
  };

  const onDelete = async (id) => {
    setLoading(true);
    const response = await deleteData(`${DAILY_LINE_PLAN}/${id}`);

    if (response && response?.code === 200) {
      message.success(
        response?.message || "Daily Line Plan Deleted Successfully",
      );
      refetch();
    } else {
      message.error("Error Deleting Daily Line Plan");
    }
    setLoading(false);
  };

  // Effects
  useEffect(() => {
    getAppRoleMenu();
  }, [currentPage, filterValues, refresh, getAppRoleMenu]);

  const tableColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      align: "center",
      width: "7%",
      render: (row) => cglFormatDate(row),
    },
    {
      title: "Line",
      dataIndex: ["line_info", "name"],
      key: "line_info_name",
      align: "center",
      width: "7%",
    },
    {
      title: "Style",
      dataIndex: ["style_info", "style_no"],
      key: "style_info_name",
      width: "12%",
    },
    {
      title: "Sub Style",
      dataIndex: ["sub_style_info", "sub_style_no"],
      key: "sub_style_no",
      width: "12%",
      render: (row) => row || "",
    },
    {
      title: "Salescontract",
      dataIndex: ["sales_contract", "reference_no"],
      key: "sales_contract_reference_no",
      width: "12%",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer_info", "name"],
      key: "buyer_info_name",
    },
    {
      title: "PO",
      dataIndex: ["po_map_info", "original_po"],
      key: "po_map_info_name",
    },
    {
      title: "Hourly Target (PC)",
      dataIndex: ["hourly_target_qty"],
      key: "hourly_target_qty",
      render: (row) => row || "N/A",
      align: "center",
    },
    {
      title: "Total SAM",
      dataIndex: ["ob_info", "total_sam", "total_updated_sam"],
      key: "ob_total_sam",
      align: "center",
      render: (_, record) => {
        return (
          record?.ob_info && (
            <span>
              {record?.ob_info?.total_updated_sam || record?.ob_info?.total_sam}
            </span>
          )
        );
      },
    },
    {
      title: "Created By",
      dataIndex: ["user_info", "name"],
      key: "created_by_name",
    },

    // {
    //   title: "PO",
    //   dataIndex: "po_map_info",
    //   key: "po_map_info_name",
    //   render:(row)=>  {
    //     return (
    //       row && <> (<span className="font-weight-bold">{row?.original_po}</span>)<span> {row?.internal_po}</span></>
    //     )
    //   }
    // },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            onClick={() => onEdit(record?.id)}
            overlay={
              <Menu
                items={[
                  
                  {
                    label: (
                      <div
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          // type="button"
                          style={{ marginLeft: 5 }}
                        >
                          Edit
                        </span>
                      </div>
                    ),
                    key: "2",
                    onClick: () => onEdit(record?.id),
                    disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
                  },
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => onDelete(record?.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <div
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <span>
                            <DeleteTwoTone twoToneColor="#ff4d4f" />
                          </span>
                          <span style={{ color: "red", marginLeft: 5 }}>
                            Delete
                          </span>
                        </div>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([DAILY_LINE_PLAN_DELETE_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={(values) => {
          setCurrentPage(1);
          setFilterValues(values || {});
        }}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="digitalization_daily_line_plan"
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onAdd}
                disabled={!hasPermission([NO_PERMISSION_REQUIRED])}
                addButtonText={
                  hasPermission([NO_PERMISSION_REQUIRED])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                hasImportButton={hasPermission([
                  DAILY_LINE_PLAN_IMPORT_PERMISSION,
                ])}
                acceptedFileTypes=".xlsx"
                uploadAPI={ImportDailyLinePlan}
              />
            }
            title={
              <Typography.Title level={5}>
                Line wise Operator assignment
              </Typography.Title>
            }
          />
        }
        content={
          <>
            <Space style={{ marginBottom: 10 }}>
              <span>Date:</span>
              <DatePicker.RangePicker size="small" value={dateRange} onChange={handleDateRangeChange} allowClear={false} />
            </Space>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                className="DailyLinePlanList-table-responsive compact-table"
                scroll={{ x: 1300, y: 600 }}
                rowKey={(record) => record?.id}
                size="small"
                bordered
                loading={loading}
                dataSource={tableData}
                locale={{
                  emptyText: (
                    <Empty
                      description={`No records between ${dateRange?.[0]?.format("DD-MMM-YYYY")} and ${dateRange?.[1]?.format(
                        "DD-MMM-YYYY",
                      )}. Widen the range above.`}
                    />
                  ),
                }}
                columns={tableColumns.map((col, idx) => ({
                  ...col,
                  fixed: idx < 4 ? "left" : undefined,
                }))}
                sticky
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default DailyLinePlanList;
